export const en = {
  nav: {
    start: "Home",
    losningar: "Solutions",
    produkter: "Services",
    kundcase: "Case Studies",
    kunskap: "Knowledge",
    information: "Information",
    nyheter: "News",
    villkor: "Terms",
    developer: "Developer",
    omGeposit: "About Geposit",
    kontakt: "Contact",
    bokademo: "Book a demo",
    loggain: "Log in",
    bestall: "Order",
  },
  hero: {
    title: "Geographic control over your business",
    subtitle:
      "Validate addresses, manage geographic areas and quality-assure your address registers across Sweden, Norway, Denmark and Finland – all in one platform. For smarter decisions and fewer errors at every step.",
    cta: "Book a demo",
    ctaSecondary: "See our solutions",
  },
  segments: {
    title: "Who we help",
    subtitle:
      "Geposit helps businesses across Sweden, Norway, Denmark and Finland to streamline their geographic planning and delivery logistics.",
    transport: {
      title: "Transport & Logistics",
      description:
        "Define delivery areas with postal code precision, ensure correct address data and reduce returns. Full control over your transport geography.",
    },
    service: {
      title: "Service & Installation",
      description:
        "Plan service areas, allocate resources geographically and give your customers exact visit times. Eliminate overlapping zones.",
    },
    ecommerce: {
      title: "E-commerce & Omni",
      description:
        "Define delivery zones, validate customer addresses in real-time and optimise your last-mile logistics. Fewer errors, happier customers.",
    },
  },
  problems: {
    title: "Does this sound familiar?",
    subtitle: "Many companies struggle with the same geographic challenges every day.",
    items: {
      areas: {
        title: "Unclear areas",
        description:
          "Geographic responsibilities are based on gut feeling instead of data. Nobody really knows where the boundaries are.",
      },
      addresses: {
        title: "Incorrect address data",
        description:
          "Packages are returned, service technicians can't find the right address and customers get frustrated by delays.",
      },
      manual: {
        title: "Manual planning",
        description:
          "Spreadsheets, maps on the wall and hours of manual work to allocate and update geographic areas.",
      },
      overview: {
        title: "Lack of overview",
        description:
          "Nobody really knows how the geographic areas look or whether they are optimally distributed and cover all needs.",
      },
    },
  },
  solutions: {
    title: "How Geposit solves it",
    subtitle:
      "Three pillars that give you full control over your geography and address quality across the Nordics.",
    pillars: {
      areas: {
        title: "Custom areas with postal code precision",
        description:
          "Create, visualise and manage your unique geographic areas with postal code polygons as building blocks. Update in real-time when needs change.",
      },
      data: {
        title: "Correct address & postal code data",
        description:
          "Always up-to-date, validated data that ensures every address is correct. Reduce returns and error costs drastically.",
      },
      control: {
        title: "Visual overview & control",
        description:
          "See your entire geography on the map. Analyse, optimise and make decisions based on data – not gut feeling.",
      },
    },
  },
  products: {
    title: "Our services",
    subtitle:
      "Three powerful tools that give you control over geography and address data – with full coverage across the Nordics.",
    atlas: {
      title: "Atlas",
      description:
        "Create and manage custom geographic areas for Sweden, Norway, Denmark and Finland. Visualise, analyse and optimise your geographic structures on an interactive map.",
      cta: "Learn more about Atlas",
    },
    valid: {
      title: "Valid",
      description:
        "Validate and enrich addresses in real-time across all Nordic countries. Ensure every address is correct before it reaches your system – via API or search service in the web interface.",
      cta: "Learn more about Valid",
    },
    adresstwatt: {
      title: "Address Cleansing",
      description:
        "Cleanse and quality-assure your existing address registers across the Nordics. Find and correct errors in bulk to improve data quality.",
      cta: "Learn more about Address Cleansing",
    },
    sisterCompanies: {
      eyebrow: "Sister companies",
      title: "Our sister companies",
      subtitle:
        "Geposit is part of the Movement Software group and works closely with two sister companies that complement our services within address data and transport optimisation.",
      postnummerservice: {
        name: "Postnummerservice",
        description:
          "Since 2005, Postnummerservice has helped individuals, companies and organisations across the Nordics with correct and up-to-date address, postal code and geodata. Together we deliver modern services built on reliable source data that is updated every week.",
        cta: "Visit postnummerservice.se",
      },
      ecoroute: {
        name: "Ecoroute",
        description:
          "Ecoroute provides smart route planning and optimisation solutions for transport and service companies that want to operate more efficiently, more sustainably and with higher profitability. Together we help our customers go from geographic planning to completed delivery.",
        cta: "Visit ecoroute.com",
      },
    },
  },
  productPages: {
    backToProducts: "All services",
    features: "Features",
    benefits: "Benefits",
    howItWorks: "How it works",
    ctaTitle: "Ready to get started?",
    ctaSubtitle: "Book a demo or order directly – we'll help you get started quickly.",
    ctaButton: "Book a demo",
    ctaOrder: "Order",
    ctaSecondary: "Contact us",
    atlas: {
      hero: {
        title: "Atlas",
        tagline: "Geographic area management",
        subtitle:
          "Create, visualise and manage your own geographic areas on an interactive map. Define sales districts, delivery zones and service areas with postal code precision – across Sweden, Norway, Denmark and Finland.",
      },
      overview:
        "Atlas is Geposit's map service for creating and managing custom geographic areas across the Nordics. Build your areas with postal code polygons as building blocks, draw freehand or import existing structures. Visualise data as heat maps, measure distances and share maps with the entire organisation – via iFrame or API. Atlas supports Sweden, Norway, Denmark and Finland.",
      featureGroups: {
        createAndVisualize: "Create & visualise with postal codes",
        calculateAndAnalyze: "Measure & analyse",
        integrateAndShare: "Integrate & share",
      },
      features: {
        title: "Everything you need for geographic planning",
        subtitle:
          "Atlas gives you the tools to take control of your geographic structures – simply, visually and data-driven.",
        items: {
          points: {
            title: "Points on map",
            description:
              "Place points by address or coordinate. Visualise customer data, warehouses or service points – individually or as a heat map.",
          },
          areas: {
            title: "Custom areas with postal codes",
            description:
              "Build areas with postal code polygons as building blocks – each postal code snaps into place like a puzzle piece. Draw freehand, import shapefiles or combine methods for full flexibility.",
          },
          heatmaps: {
            title: "Heat maps",
            description:
              "Visualise data patterns geographically with heat maps. See concentrations of customers, deliveries or service needs directly on the map.",
          },
          routes: {
            title: "Distance calculation",
            description:
              "Calculate distances and travel times between points. Choose vehicle type for realistic estimates – a support for planning service areas and catchment zones.",
          },
          measure: {
            title: "Measurement tools",
            description:
              "Use range circles to measure distances from a point. Perfect for defining catchment areas or service radius.",
          },
          attributes: {
            title: "Attributes & metadata",
            description:
              "Attach your own data to areas and points. Add price zones, responsible salespeople, capacity or other business rules.",
          },
          iframe: {
            title: "iFrame sharing",
            description:
              "Embed your maps directly on a website or in an internal system. Give colleagues and partners access without login.",
          },
          api: {
            title: "API integration",
            description:
              "Fetch and send data programmatically via REST API. Integrate Atlas with your CRM, ERP, transport system or BI platform.",
          },
        },
      },
      useCases: {
        title: "Use cases",
        items: {
          salesDistricts: {
            title: "Sales districts",
            description:
              "Define and visualise sales territories. See coverage, identify gaps and balance workloads.",
          },
          deliveryZones: {
            title: "Delivery zones",
            description:
              "Create clear delivery areas per terminal, driver or region. Everyone sees the same picture.",
          },
          priceZones: {
            title: "Price zones",
            description:
              "Define geographic zones with different pricing. Connect to your business system for automatic price calculation.",
          },
          biConnection: {
            title: "BI & analytics",
            description:
              "Export geodata to your BI tool or connect directly via API. Geographic dimension in your reports.",
          },
          distanceCalc: {
            title: "Distance calculation",
            description:
              "Calculate distances and travel times between points with different vehicle types. A support for planning and scheduling.",
          },
          crmConnection: {
            title: "CRM connection",
            description:
              "Synchronise geographic areas with your CRM system. Automatic assignment of customers to the right salesperson.",
          },
        },
      },
      benefits: {
        title: "Why Atlas?",
        items: {
          time: {
            title: "Save time",
            description:
              "Stop using manual spreadsheets and paper maps. Do in minutes what previously took hours.",
          },
          decisions: {
            title: "Better decisions",
            description:
              "Base your geographic decisions on data instead of gut feeling. See patterns and optimisation opportunities directly.",
          },
          overview: {
            title: "Full overview",
            description:
              "Everyone in the organisation sees the same picture. No more ambiguity about where the boundaries are.",
          },
        },
      },
      howItWorks: {
        steps: {
          first: {
            title: "Import your data",
            description:
              "Upload your existing areas, addresses or coordinates. Atlas supports shapefiles, CSV and API import.",
          },
          second: {
            title: "Create and customise",
            description:
              "Build areas with postal code polygons as building blocks, draw freehand, visualise with heat maps and measure distances.",
          },
          third: {
            title: "Share and integrate",
            description:
              "Publish maps via iFrame, export data or connect to your systems via API.",
          },
        },
      },
      pricing: {
        features: {
          binding12: "12-month commitment",
          adminMapCountry: "1 admin, 1 map, 1 country",
          importData: "Import custom data on map",
          manageAreas: "Create, view and manage areas",
          apiIntegration: "Integrate with your systems via API",
          export2000: "Export 2,000 postal codes/month",
          exportUnlimited: "Export unlimited postal codes",
          routingIncluded: "Routing Small included",
        },
        plans: {
          small: {
            name: "SMALL",
            price: "1,795 SEK",
          },
          medium: {
            name: "MEDIUM",
            price: "2,995 SEK",
          },
          medium3: {
            name: "MEDIUM 3-MO",
            price: "3,595 SEK",
          },
        },
        addons: {
          extraCountry: { name: "Extra country", price: "1,000 SEK/mo" },
          extraMap: { name: "Extra map", price: "495 SEK/mo" },
          extraAdmin: { name: "Extra admin user", price: "295 SEK/mo" },
          extraViewer: { name: "Extra viewer", price: "95 SEK/mo" },
          exportPostal: { name: "Postal code export", small: "0.80 SEK/pc", mediumPlus: "Included" },
          exportCoords: { name: "Coordinate export", small: "1.00 SEK/pc", mediumPlus: "0.90 SEK/pc" },
        },
      },
      routing: {
        title: "Routing",
        subtitle: "Add-on service for Atlas providing route calculation and reach area analysis. Calculate routes, driving times and reach areas – and create even more realistic area divisions.",
        routes: {
          title: "Route calculation",
          description: "Calculate driving times, distances and optimal routes between points on the map. Support for multi-stop and different vehicle types for realistic estimates.",
        },
        reach: {
          title: "Reach areas",
          description: "Calculate and visualise how large a geographic area you can reach within a certain time from a given point. Perfect for creating realistic responsibility areas and catchment zones.",
        },
        tabs: {
          routes: "Routes",
          reach: "Reach",
        },
        tiers: {
          sectionTitle: "Routing packages",
          included: "Routing Small is included in Atlas Medium and Atlas Medium 3-mo. Atlas Small customers can add Routing as a complement.",
          volumeTitle: "Included volumes",
          small: {
            name: "Small",
            price: "495 SEK/mo",
            features: [
              "Calculate driving distance and time between two points",
              "Analyze and create reach areas",
            ],
            volumes: [
              "10,000 route calls/mo",
              "250 reach calculations/mo",
            ],
          },
          medium: {
            name: "Medium",
            price: "995 SEK/mo",
            features: [
              "Calculate driving distance and time between two or more points",
              "Analyze and create reach areas",
              "Optimize route planning between multiple points (multi-stop)",
              "Advanced transport and vehicle configurations",
            ],
            volumes: [
              "15,000 route calls/mo",
              "1,000 reach calculations/mo",
              "5,000 optimize calls/mo",
            ],
          },
          large: {
            name: "Large",
            price: "1,495 SEK/mo",
            features: [
              "Calculate driving distance and time between two or more points",
              "Analyze and create reach areas",
              "Optimize route planning between multiple points (multi-stop)",
              "Advanced transport and vehicle configurations",
            ],
            volumes: [
              "25,000 route calls/mo",
              "2,500 reach calculations/mo",
              "10,000 optimize calls/mo",
            ],
          },
        },
      },
    },
    valid: {
      hero: {
        title: "Valid",
        tagline: "Address validation & enrichment",
        subtitle:
          "Validate, correct and enrich addresses in real-time for Sweden, Norway, Denmark and Finland. Via API, autocomplete in forms or search in the web interface – Valid ensures correct address data at every step.",
      },
      overview:
        "Valid is Geposit's service for address validation, autocomplete and enrichment with complete Nordic coverage. Check whether an address exists in Sweden, Norway, Denmark or Finland, give users smart suggestions as they type, and enrich every address with over 10 geographic attributes. Available via REST API or as a search service in the web interface.",
      featureGroups: {
        api: "API services",
        web: "Web interface",
      },
      features: {
        title: "Correct address data – every time",
        subtitle:
          "Valid catches errors before they become problems. Integrate into your systems or use the search service directly in the browser.",
        items: {
          validation: {
            title: "Address validation",
            description:
              "Check whether an address exists and is correct. Get immediate feedback with status indication and correction suggestions.",
          },
          autocomplete: {
            title: "Autocomplete",
            description:
              "Give users smart address suggestions in real-time as they type. Faster filling, fewer errors, better user experience.",
          },
          enrichment: {
            title: "Enrichment",
            description:
              "Complement every address with over 10 attributes: county, municipality, district, parish, coordinates, property designation, DeSO/RegSO and more.",
          },
          search: {
            title: "Search in web interface",
            description:
              "Search and verify addresses directly in Geposit's web interface – without needing to integrate via API. Perfect for manual checks.",
          },
          coverage: {
            title: "Nordic coverage",
            description:
              "Complete address database for Sweden, Norway, Denmark and Finland. Updated weekly with data from official sources.",
          },
        },
      },
      enrichmentData: {
        title: "Enrichment attributes",
        subtitle:
          "Each validated address can be enriched with the following geographic and administrative attributes.",
        items: {
          county: {
            name: "County",
            description: "Administrative county the address belongs to",
          },
          municipality: {
            name: "Municipality",
            description: "Municipality the address is registered in",
          },
          district: {
            name: "District",
            description: "Geographic district within the municipality",
          },
          parish: {
            name: "Parish",
            description: "Church parish for the address",
          },
          lkfCode: {
            name: "LKF code",
            description:
              "Statistical area code (County, Municipality, Parish)",
          },
          municipalityCode: {
            name: "Municipality code",
            description: "Official numeric municipality code",
          },
          property: {
            name: "Property designation",
            description:
              "Official property designation from Lantmäteriet",
          },
          coordinates: {
            name: "Coordinates",
            description:
              "Latitude and longitude (WGS84) for the address",
          },
          deso: {
            name: "DeSO",
            description: "Demographic statistical area (SCB)",
          },
          regso: {
            name: "RegSO",
            description: "Regional statistical area (SCB)",
          },
          uuid: {
            name: "UUID",
            description: "Unique identifier for the address",
          },
          postalCode: {
            name: "Postal code",
            description: "Verified and current postal code",
          },
          city: {
            name: "City",
            description:
              "Official postal city linked to the postal code",
          },
        },
      },
      useCases: {
        title: "Use cases",
        items: {
          checkout: {
            title: "E-commerce checkout",
            description:
              "Integrate autocomplete and validation in the checkout. The customer fills in the correct address directly – fewer returns, faster delivery.",
          },
          booking: {
            title: "Booking portals",
            description:
              "Ensure correct addresses during service booking. The technician finds the right location directly without unnecessary calls.",
          },
          systemIntegration: {
            title: "System integration",
            description:
              "Validate and enrich addresses when importing to CRM, ERP or marketing systems. Ensure data quality from the start.",
          },
          crmQuality: {
            title: "CRM quality",
            description:
              "Run your customer registers through Valid to identify and correct incorrect addresses. Better data, better decisions.",
          },
        },
      },
      benefits: {
        title: "Why Valid?",
        items: {
          returns: {
            title: "Fewer returns",
            description:
              "Correct address data from the start means fewer letters and packages coming back.",
          },
          experience: {
            title: "Better customer experience",
            description:
              "Faster forms with autocomplete and instant validation. Your customers appreciate it.",
          },
          costs: {
            title: "Lower costs",
            description:
              "Every incorrect address costs you money. Valid pays for itself through reduced error costs.",
          },
        },
      },
      howItWorks: {
        steps: {
          first: {
            title: "Integrate or search",
            description:
              "Connect Valid to your system via REST API, or use the search service directly in the web interface.",
          },
          second: {
            title: "Validate and enrich",
            description:
              "Addresses are validated in real-time. Incorrect addresses are corrected and enriched with 10+ attributes automatically.",
          },
          third: {
            title: "Correct data – always",
            description:
              "Every address in your system is verified, complete and linked to current geographic data.",
          },
        },
      },
      demo: {
        title: "Try Valid",
        subtitle: "Try the service directly – select a country, type an address and see how Valid validates, suggests and enriches in real-time.",
        countryLabel: "Select country:",
        countries: {
          se: "Sweden",
          no: "Norway",
          dk: "Denmark",
          fi: "Finland",
        },
        tabs: {
          autocomplete: "Autocomplete",
          validate: "Validate",
          enrich: "Enrich",
        },
        autocomplete: {
          description: "Start typing an address and see suggestions in real-time. Select a suggestion to fill in the address.",
          placeholder: "Start typing an address…",
          selected: "Selected address",
        },
        validate: {
          description: "Enter an address and click Validate to check if it exists and is correct.",
          streetLabel: "Street address",
          streetPlaceholder: "e.g. Storgatan 1",
          postcodeLabel: "Postal code",
          postcodePlaceholder: "e.g. 111 22",
          localityLabel: "City",
          localityPlaceholder: "e.g. Stockholm",
          button: "Validate address",
          validMessage: "The address is correct!",
          invalidMessage: "The address could not be validated",
          suggestions: "Did you mean:",
        },
        enrich: {
          description: "Enter an address and click Enrich to see all associated geographic and administrative data.",
          button: "Enrich address",
          errorMessage: "The address could not be enriched. Check that it is correct.",
        },
      },
      pricing: {
        features: {
          nordicValidation: "Address validation across the Nordics",
          autocomplete: "Autocomplete for street names, postal codes and cities",
          adminControl: "Control of county, municipality, parish and district",
        },
        details: {
          validationsIncluded: "Validations/mo",
          autocompleteIncluded: "Autocomplete/mo",
          extraValidation: "Extra validation",
          extraAutocomplete: "Extra autocomplete",
        },
        plans: {
          small: {
            name: "SMALL",
            price: "695 SEK",
            validations: "1,000",
            autocomplete: "7,500",
            extraValidation: "0.50 SEK",
            extraAutocomplete: "0.07 SEK",
          },
          medium: {
            name: "MEDIUM",
            price: "1,795 SEK",
            validations: "6,000",
            autocomplete: "45,000",
            extraValidation: "0.35 SEK",
            extraAutocomplete: "0.05 SEK",
          },
          large: {
            name: "LARGE",
            price: "3,995 SEK",
            validations: "20,000",
            autocomplete: "150,000",
            extraValidation: "0.25 SEK",
            extraAutocomplete: "0.03 SEK",
          },
          xl: {
            name: "XL",
            price: "5,995 SEK",
            validations: "40,000",
            autocomplete: "300,000",
            extraValidation: "0.15 SEK",
            extraAutocomplete: "0.01 SEK",
          },
        },
        addons: {
          geocodePostal: { name: "Geocoding postal code", small: "1.00 SEK/pc", medium: "0.90 SEK/pc", large: "0.80 SEK/pc", xl: "0.70 SEK/pc" },
          geocodeAddress: { name: "Geocoding address", small: "By quote", medium: "By quote", large: "By quote", xl: "By quote" },
          uuid: { name: "UUID", small: "0.10 SEK/pc", medium: "0.08 SEK/pc", large: "0.06 SEK/pc", xl: "0.04 SEK/pc" },
          property: { name: "Property designation", small: "0.50 SEK/pc", medium: "0.40 SEK/pc", large: "0.30 SEK/pc", xl: "0.20 SEK/pc" },
          desoRegso: { name: "DeSO/RegSO", small: "0.60 SEK/pc", medium: "0.50 SEK/pc", large: "0.40 SEK/pc", xl: "0.30 SEK/pc" },
        },
      },
    },
    adresstwatt: {
      hero: {
        title: "Address Cleansing",
        tagline: "Address register maintenance",
        subtitle:
          "Submit your address register – get back a cleansed, corrected and enriched version. We identify incorrect addresses, duplicates and outdated information across Sweden, Norway, Denmark and Finland.",
      },
      overview:
        "Address Cleansing is Geposit's service for quality-assuring existing address registers across the Nordics. You send in your register in any format, and we analyse every address against our updated database for Sweden, Norway, Denmark and Finland. Incorrect addresses are corrected, duplicates identified and every address enriched with the same attributes Valid delivers – from county and municipality to coordinates and DeSO/RegSO.",
      featureGroups: {
        analyze: "Analyse & identify",
        correctAndEnrich: "Correct & enrich",
      },
      features: {
        title: "Clean address data – across the entire register",
        subtitle:
          "Address Cleansing takes care of your existing address data and ensures it maintains high quality.",
        items: {
          analyze: {
            title: "Register analysis",
            description:
              "We analyse your existing register and identify incorrect, incomplete and outdated addresses. You receive a complete quality report.",
          },
          correct: {
            title: "Correction against official data",
            description:
              "Incorrect addresses are automatically corrected against our updated database of official addresses from Nordic sources.",
          },
          duplicates: {
            title: "Duplicate cleansing",
            description:
              "Identify and handle duplicates in your registers. Merge, mark or remove to get a clean customer register.",
          },
          enrich: {
            title: "Enrichment with attributes",
            description:
              "Each address is enriched with the same attributes as Valid: county, municipality, coordinates, property designation, DeSO/RegSO and more.",
          },
          report: {
            title: "Quality report",
            description:
              "Get a detailed report with statistics on the number of corrected, removed and enriched addresses. See exactly what was done.",
          },
          flexible: {
            title: "Flexible formats",
            description:
              "Send your addresses in CSV, Excel or via API. Get back the cleansed version in the same format – ready to import.",
          },
        },
      },
      useCases: {
        title: "Use cases",
        items: {
          registerCare: {
            title: "Register maintenance",
            description:
              "Regular cleansing of customer registers to keep data quality high. Run quarterly or before important campaigns.",
          },
          campaigns: {
            title: "Campaign mailings",
            description:
              "Cleanse the register before physical mailings. Avoid the cost of letters that don't reach their destination due to incorrect addresses.",
          },
          gdpr: {
            title: "GDPR & data quality",
            description:
              "Identify and cleanse outdated addresses as part of your GDPR work. Only keep the data you actually need.",
          },
        },
      },
      benefits: {
        title: "Why Address Cleansing?",
        items: {
          quality: {
            title: "Higher data quality",
            description:
              "A clean address register is the foundation for all logistics, marketing and customer communication.",
          },
          savings: {
            title: "Direct savings",
            description:
              "Every corrected address reduces the cost of returns, incorrect mailings and manual handling.",
          },
          compliance: {
            title: "Better customer data",
            description:
              "Correct addresses improve your CRM data and provide better basis for business decisions.",
          },
        },
      },
      howItWorks: {
        steps: {
          first: {
            title: "Submit your register",
            description:
              "Upload your address register in CSV, Excel or via API. We support all common formats.",
          },
          second: {
            title: "We analyse and cleanse",
            description:
              "Each address is checked, corrected against official data, enriched with attributes and duplicates are removed.",
          },
          third: {
            title: "Get back a clean register",
            description:
              "You receive your register back – corrected, enriched and with a detailed quality report.",
          },
        },
      },
      pricing: {
        tagline: "PRICING",
        title: "Simple and transparent pricing",
        subtitle: "Start with a free analysis of your register. If you want to proceed with a full cleanse, you pay per address row – the more addresses, the lower the price.",
        descriptionCol: "Description",
        freeAnalysis: "Free analysis",
        fullWash: "Address Cleansing",
        volumeCol: "Volume",
        setupFee: "Setup fee",
        setupFreeValue: "SEK 0",
        setupWashValue: "SEK 500",
        rows: "address rows",
        features: {
          statistics: "Statistics – summary of incorrect addresses and which errors should be corrected",
          identify: "Identification of which addresses are incorrect",
          correct: "Incorrect addresses corrected against official sources",
          split: "Street address split into street name, number, letter etc.",
          suggest: "Suggestions for correct address when multiple matches exist",
          enrich: "Addresses enriched with county, municipality and parish",
        },
        tiers: {
          tier1: { free: "No cost", wash: "SEK 0.30/row" },
          tier2: { free: "No cost", wash: "SEK 0.20/row" },
          tier3: { free: "No cost", wash: "SEK 0.10/row" },
        },
      },
    },
    pricing: {
      tagline: "PLANS AND PRICING",
      title: "Choose the plan that fits you",
      subtitle: "All plans include support and access to our documentation. Choose the plan that suits you and contact us if you have any questions.",
      recommended: "Recommended",
      cta: "Get started",
      ctaHighlighted: "Get started",
      showAddons: "Show add-ons and variable pricing",
      hideAddons: "Hide add-ons",
      addonColumn: "Add-on",
      perMonth: "/ mo",
      shortBinding: "3-month commitment",
    },
  },
  solutionPages: {
    backToSolutions: "All solutions",
    ctaButton: "Book a demo",
    ctaSecondary: "See our services",
    readMore: "Read more",
    overview: {
      tagline: "SOLUTIONS",
      title: "Solutions for your industry",
      subtitle: "Whether you work with transport, service or e-commerce – Geposit gives you the tools to optimise your geography and address data.",
      segments: {
        transport: {
          title: "Transport & Logistics",
          description: "Optimise delivery areas, reduce returns and ensure every delivery reaches the right address. Define your own zones and get full control over your transport geography.",
        },
        service: {
          title: "Service & Installation",
          description: "Plan service areas without overlap, allocate resources efficiently and give your customers exact visit times based on geographic data.",
        },
        ecommerce: {
          title: "E-commerce & Omni",
          description: "Validate customer addresses in real-time, define delivery zones and optimise your last-mile logistics for happier customers and fewer returns.",
        },
      },
      cta: {
        title: "Unsure which solution fits?",
        subtitle: "Contact us and we'll help you find the right solution for your business.",
      },
    },
    transport: {
      hero: {
        tagline: "Transport & Logistics",
        title: "More efficient deliveries with geographic control",
        subtitle: "Define delivery areas with postal code precision, ensure correct address data and reduce returns – so every delivery reaches the right recipient, on time.",
      },
      challenges: {
        title: "Challenges in transport",
        subtitle: "Transport companies struggle daily with geographic and address-related problems that cost time and money.",
        items: {
          zones: {
            title: "Unclear delivery zones",
            description: "Without clearly defined delivery areas, packages end up in the wrong place, zones overlap and drivers waste time sorting out boundaries.",
          },
          routes: {
            title: "Unclear boundaries",
            description: "Without data-driven zone planning, responsibility areas are based on gut feeling. This leads to overlapping zones and gaps in coverage.",
          },
          returns: {
            title: "High return costs",
            description: "Incorrect or incomplete addresses lead to failed deliveries. Every return costs money and worsens the customer experience.",
          },
        },
      },
      solutions: {
        title: "How Geposit helps transport companies",
        subtitle: "With the right tools, you can take control of your delivery geography and reduce costly errors.",
        items: {
          areas: {
            title: "Define delivery areas in Atlas",
            description: "Create and visualise your own delivery zones in Atlas with postal code polygons as building blocks. No more unclear boundaries – everyone sees the same picture.",
          },
          data: {
            title: "Validate addresses with Valid",
            description: "With Valid, every address is validated before delivery. Incorrect addresses are caught directly at entry, avoiding costly returns.",
          },
          optimize: {
            title: "Keep the register clean with Address Cleansing",
            description: "Regularly cleanse your customer registers. Analyse your delivery areas and ensure zone divisions match reality.",
          },
        },
      },
      products: {
        title: "Services for transport",
        subtitle: "Three tools that together give you full control over delivery geography and address quality.",
        items: {
          atlas: {
            name: "Atlas",
            description: "Create, visualise and manage your delivery zones with postal code polygons as building blocks. Define areas per driver, terminal or region.",
          },
          valid: {
            name: "Valid",
            description: "Validate delivery addresses in real-time. Catch incorrect addresses before the package leaves the terminal and avoid costly returns.",
          },
          adresstwatt: {
            name: "Address Cleansing",
            description: "Regularly cleanse your customer registers to keep address data correct and up-to-date. Reduce error costs and increase delivery precision.",
          },
        },
      },
      cta: {
        title: "Ready to streamline your deliveries?",
        subtitle: "Book a demo and see how Geposit can help your transport company reduce returns and optimise delivery areas.",
      },
    },
    service: {
      hero: {
        tagline: "Service & Installation",
        title: "Smarter planning of service areas",
        subtitle: "Allocate resources geographically, eliminate overlapping zones and give your customers exact visit times.",
      },
      challenges: {
        title: "Challenges in service",
        subtitle: "Service and installation companies face daily geographic challenges that affect efficiency and customer satisfaction.",
        items: {
          overlap: {
            title: "Overlapping zones",
            description: "Without clear boundaries between service areas, you risk sending multiple technicians to the same area while others are understaffed.",
          },
          resources: {
            title: "Uneven resource allocation",
            description: "Some technicians have too large areas, others too small. Without geographic analysis, allocation is based on gut feeling.",
          },
          scheduling: {
            title: "Imprecise scheduling",
            description: "Without correct addresses and distance calculations, time windows become unreliable, leading to dissatisfied customers.",
          },
        },
      },
      solutions: {
        title: "How Geposit helps service companies",
        subtitle: "With Geposit's tools, you can plan, visualise and optimise your service areas based on data.",
        items: {
          zones: {
            title: "Clear service areas in Atlas",
            description: "Define exact service areas in Atlas with postal code polygons – no overlaps, no gaps. Every technician knows their exact responsibility area.",
          },
          address: {
            title: "Find the right place with Valid",
            description: "With Valid, every customer address is verified at booking. The technician finds the right location directly, avoiding costly wasted trips.",
          },
          balance: {
            title: "Clean data with Address Cleansing",
            description: "Cleanse your customer registers and analyse your service areas with supporting data such as customer count, distances and travel times.",
          },
        },
      },
      products: {
        title: "Services for service",
        subtitle: "Tools that give your service team the right conditions from the start.",
        items: {
          atlas: {
            name: "Atlas",
            description: "Create and manage service areas visually with postal codes as foundation. Allocate technicians per zone and see your entire service geography on one map.",
          },
          valid: {
            name: "Valid",
            description: "Validate customer addresses at booking. Avoid wasted trips by ensuring every service address is correct.",
          },
          adresstwatt: {
            name: "Address Cleansing",
            description: "Cleanse your customer register from incorrect addresses. Ensure your service platform is built on correct and up-to-date data.",
          },
        },
      },
      cta: {
        title: "Ready to optimise your service areas?",
        subtitle: "Book a demo and see how Geposit can help your service company plan smarter and deliver better.",
      },
    },
    ecommerce: {
      hero: {
        tagline: "E-commerce & Omni",
        title: "Perfect delivery in every channel",
        subtitle: "Validate customer addresses directly at checkout, define delivery zones and ensure every order reaches the right recipient – regardless of channel.",
      },
      challenges: {
        title: "Challenges in e-commerce",
        subtitle: "E-commerce and omnichannel companies struggle with address quality and delivery precision in an increasingly demanding market.",
        items: {
          delivery: {
            title: "Unclear delivery zones",
            description: "Without defined delivery areas, it's difficult to offer differentiated shipping options and pricing per zone.",
          },
          returns: {
            title: "Returns due to address errors",
            description: "Customers enter incorrect addresses, and without validation the package is sent anyway. Result: returns, costs and unhappy customers.",
          },
          expansion: {
            title: "Nordic expansion",
            description: "Expanding to new Nordic markets requires correct address data for each country. Incorrect data hinders growth and increases costs.",
          },
        },
      },
      solutions: {
        title: "How Geposit helps e-commerce",
        subtitle: "With the right data and tools, you can reduce returns, improve the customer experience and expand more safely.",
        items: {
          zones: {
            title: "Smart delivery zones in Atlas",
            description: "Define your delivery areas in Atlas with postal code polygons and connect them to your shipping options. Offer the right delivery choice based on the customer's address – automatically.",
          },
          validation: {
            title: "Address validation with Valid at checkout",
            description: "With Valid, the customer's address is validated directly at entry. Autocomplete provides smart suggestions and incorrect addresses are corrected before the order is created.",
          },
          quality: {
            title: "Clean customer data with Address Cleansing",
            description: "Regularly cleanse your customer registers. Keep your CRM data clean and updated for better marketing and delivery precision.",
          },
        },
      },
      products: {
        title: "Services for e-commerce",
        subtitle: "Everything you need for correct address data and optimised delivery logistics.",
        items: {
          atlas: {
            name: "Atlas",
            description: "Define delivery zones per region or shipping method with postal code polygons as foundation. See your delivery areas on the map and optimise your shipping structure.",
          },
          valid: {
            name: "Valid",
            description: "Integrate address validation directly in your checkout. Give customers the right address suggestions and avoid delivery problems from the start.",
          },
          adresstwatt: {
            name: "Address Cleansing",
            description: "Quality-assure your customer registers and keep your data fresh. Perfect for regular cleansing before campaigns and mailings.",
          },
        },
      },
      cta: {
        title: "Ready to improve your delivery precision?",
        subtitle: "Book a demo and see how Geposit can help your e-commerce reduce returns and validate addresses in real-time.",
      },
    },
  },
  socialProof: {
    logoTitle: "Over 900 companies trust Geposit",
    title: "What our customers say",
    subtitle: "We help leading Nordic companies take control of their geography.",
    testimonials: {
      first: {
        quote:
          "Now we have far fewer areas where we lack coverage for our services. Before, we had 'blind spots' in areas we could have served. The overview in Atlas has made our catchment areas both clearer and simpler.",
        author: "Björn Nilsson",
        company: "Web System Manager, Elon",
      },
      second: {
        quote:
          "Smooth service for specifying delivery areas and dividing them into different zones. Easy to export postal code lists for each area.",
        author: "Julian Lee",
        company: "Founder, Airmee",
      },
    },
  },
  customerCases: {
    hero: {
      title: "Case Studies",
      subtitle: "See how Nordic companies use Geposit to optimise their geography, reduce returns and streamline their logistics.",
    },
    logos: {
      title: "Companies using Geposit",
      subtitle: "Over 900 companies, organisations and authorities trust our data.",
    },
    featured: {
      tagline: "Case study",
      title: "Faster deliveries for the market leader",
      company: "Best Transport",
      companyDesc: "Market-leading provider of fast deliveries in the Nordics.",
      description: "Best Transport is a market-leading provider of fast deliveries in the Nordics. To meet their customers' demands for fast and accurate deliveries, high requirements are placed on well-planned and clear geographic zones.",
      result: "With the help of the Atlas map service as a unified planning tool, Best Transport could create both a better overview and optimal distribution zones. The result was higher vehicle fill rates and thus improved profitability, reduced carbon emissions – and happier customers!",
      stats: {
        efficiency: {
          value: "Fill rate",
          label: "Higher vehicle fill rate",
        },
        emissions: {
          value: "CO₂",
          label: "Reduced carbon emissions",
        },
        satisfaction: {
          value: "Satisfaction",
          label: "Happier end customers",
        },
      },
      bullets: {
        challenge: "Complex zone structure with many overlapping services",
        solution: "Atlas as unified planning tool for zone division",
        outcome: "Optimal distribution zones with higher fill rates",
      },
    },
    testimonials: {
      title: "What our customers say",
      elon: {
        quote: "Now we have far fewer areas where we lack coverage for our services. Before, we had 'blind spots' in areas we could have served. The overview in Atlas has made our catchment areas both clearer and simpler.",
        author: "Björn Nilsson",
        role: "Web System Manager",
        company: "Elon",
      },
      airmee: {
        quote: "Smooth service for specifying delivery areas and dividing them into different zones. Easy to export postal code lists for each area.",
        author: "Julian Lee",
        role: "Founder",
        company: "Airmee",
      },
    },
    cta: {
      title: "Want to be the next success story?",
      subtitle: "Book a demo and see how Geposit can streamline your business.",
      button: "Book a demo",
    },
  },
  cta: {
    title: "Ready to take control?",
    subtitle:
      "Talk to us about how Geposit can help your business optimise geography and address quality.",
    form: {
      company: "Company name",
      name: "Name",
      email: "Email",
      role: "Role",
      interest: "What would you like to know more about?",
      interestOptions: {
        atlas: "Atlas – Geographic areas",
        valid: "Valid – Address validation",
        adresstwatt: "Address Cleansing",
        general: "General information",
      },
      message: "Message (optional)",
      submit: "Send enquiry",
      success: "Thank you for your enquiry! We will get back to you shortly.",
    },
  },
  footer: {
    description:
      "Geposit is the Nordics' leading platform for address and geodata. With correct and up-to-date source data, we offer web-based services, integrations via API and data on file.",
    quickLinks: "Quick links",
    products: "Services",
    contact: "Contact",
    address: "Biblioteksgatan 29, 114 35 Stockholm",
    email: "info@geposit.se",
    phone: "+46 8 410 239 70",
    partOf: "Part of Movement Software",
    progritsDesc:
      "Since 2015, Geposit is part of Movement Software (formerly Progrits) – an international software group focused on e-commerce, logistics and vehicles.",
    sister: "Sister companies",
    postnummerservice: "Postnummerservice",
    postnummerserviceDesc:
      "Postnummerservice has since 2005 helped individuals, companies and organisations in the Nordics with correct and up-to-date address, postal code and geodata.",
    ecoroute: "Ecoroute",
    ecorouteDesc:
      "Ecoroute provides smart route planning and optimisation solutions for transport and service companies that want to operate more efficiently, more sustainably and with higher profitability.",
    copyright: "© 2026 Geposit AB. All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms",
  },
  about: {
    hero: {
      title: "About Geposit",
      subtitle: "From Swedish reseller to Nordic SaaS company in geodata",
    },
    story: {
      title: "Our story",
      p1: "Geposit is a Nordic provider of digital services in address and geodata. Our journey began in 2005 under the name Postnummerservice, when we delivered updated address and postal code data to our customers.",
      p2: "In 2010 we launched automatic address verification and the following year came our first digital map service for zone division. Today we offer digital services that simplify, quality-assure and automate the handling of addresses and geographic data.",
      p3: "Today our services are used by companies across the Nordics. Whether you need address cleansing, smart zone division or geodata services via API, our solutions are always built on correct and up-to-date source data.",
    },
    data: {
      title: "The Nordics' most reliable address data",
      description: "Did you know that addresses and postal codes change every week? New residential areas emerge, roads are rebuilt and cities change. Every year, approximately 250,000 addresses and postal codes are updated in Sweden.",
      description2: "At Geposit we continuously collect and process data from reliable and official sources such as national postal operators, land surveys and authorities. Thanks to the high quality of our data deliveries, we are today a valued partner to over 900 companies, organisations and authorities.",
      stats: {
        customers: "+5,000",
        customersLabel: "Satisfied customers since 2005",
        countries: "4",
        countriesLabel: "Nordic countries",
        addresses: "250,000",
        addressesLabel: "Address changes per year in Sweden",
      },
    },
    values: {
      title: "What drives us",
      innovation: {
        title: "Technical innovation",
        description: "We constantly develop new solutions with customer value at the centre. Our technology is built on reliable and up-to-date source data.",
      },
      trust: {
        title: "Trust and collaboration",
        description: "We believe that trust, inclusion and an encouraging climate lead to engagement and shared success.",
      },
      quality: {
        title: "Quality in every detail",
        description: "Our data is updated weekly from official sources. We never compromise on quality.",
      },
    },
    progrits: {
      title: "Part of Movement Software",
      description: "Since 2015, Geposit is part of Movement Software (formerly Progrits) – an international group of companies in niche software and information services for business to business.",
      description2: "Within Movement Software, we collaborate with other B2B companies to create stronger customer offerings and better practices.",
      tagline: "WE MOVE YOUR WORLD",
      link: "Visit movementsoftware.com",
    },
    postnummerservice: {
      title: "Sister company: Postnummerservice",
      description: "Postnummerservice has since 2005 helped individuals, companies and organisations in the Nordics by providing correct and up-to-date address, postal code and geodata.",
      description2: "Postnummerservice collects and processes data from reliable sources such as national postal operators, land surveys and official authorities. Data is updated weekly to guarantee high quality and accuracy.",
      link: "Visit postnummerservice.se",
    },
    ecoroute: {
      title: "Sister company: Ecoroute",
      description: "Ecoroute develops smart route planning and optimisation for transport and logistics companies in the Nordics. With a focus on efficiency and sustainability, they help their customers reduce mileage, lower costs and cut climate impact.",
      description2: "Within Movement Software's Transportation & Logistics focus area, we combine Ecoroute's route optimisation with Geposit's address and geodata services to deliver end-to-end solutions across the delivery chain.",
      link: "Visit ecoroute.com",
    },
    careers: {
      title: "Join Geposit",
      description: "We're growing and always looking for new talent. We are driven by technical innovation, with the requirement that customer value always comes first.",
      cta: "Contact us",
    },
  },
  information: {
    title: "Information",
    subtitle: "News, documents and important information from Geposit.",
    news: {
      title: "News",
      subtitle: "Latest from Geposit – updates, improvements and news.",
      empty: "No news published yet.",
    },
    documents: {
      title: "Documents and terms",
      subtitle: "Here you'll find our general terms, policies and other documents.",
    },
    quickNav: {
      news: "News",
      documents: "Documents & terms",
    },
    readMore: "Read more",
    categories: {
      villkor: "Terms",
      policy: "Policy",
      ovrigt: "Other",
    },
  },
  cookieBanner: {
    title: "We use cookies",
    description: "We use cookies to improve your experience on our website, analyse traffic and personalise content.",
    readMore: "Read our cookie policy",
    accept: "Accept",
    reject: "Reject",
  },
  cookiePolicy: {
    title: "Cookie Policy",
    lastUpdated: "Last updated: 2024-03-18",
    intro: "We collect information using technology such as cookies (e.g. on your browser or device). Cookies are used to store and receive IDs and other information on computers, phones and other devices. Cookies can, for example, help us recognise you the next time you visit our website, but also enable us to offer a more secure and reliable service.",
    purposeTitle: "We use cookies for the following purposes",
    types: {
      functional: {
        title: "Functional cookies",
        description: "We use functional cookies to manage certain features on our pages, so that your choices and settings will be remembered when you return to the website.",
      },
      analytics: {
        title: "Analytics cookies",
        description: "We use analytics cookies to measure how our services are requested, used and perform when in use. The information we collect is used to maintain and improve our service.",
      },
      thirdParty: {
        title: "Third-party cookies",
        description: "We may also allow our partners to use cookies in our services for the same purposes described above. We may also allow third parties that offer services on our behalf to use cookies for the purposes described above.",
      },
    },
    managingTitle: "Managing cookies",
    managingText: "Most browsers allow the user to choose how cookies are handled. You can set your browser to refuse cookies, or to delete certain cookies. Here you can find information about how to manage cookies in your browser:",
    blockingWarning: "If you choose to block cookies, parts of our service's functionality may be impaired or disappear, as it requires cookies to be allowed.",
    changesTitle: "Changes to this policy",
    changesText: "Geposit may from time to time change this cookie policy due to changed practices for the use of cookies and processing of data, and to comply with statutory requirements.",
    contactTitle: "Questions",
    contactText: "If you have any questions or concerns about our use of cookies, please contact us.",
  },
  notFound: {
    title: "404",
    message: "Page not found",
    backHome: "Return to home",
  },
  kontakt: {
    title: "Contact",
    hero: {
      title: "How can we help you?",
      subtitle: "Book a free demo or get in touch with your questions – we'll help you find the right solution.",
    },
    types: {
      demo: {
        title: "Book a demo",
        description: "Get a free walkthrough of our services, tailored to your business.",
      },
      contact: {
        title: "Contact us",
        description: "Have questions about our products, pricing or partnerships? Get in touch!",
      },
    },
    contactInfo: {
      title: "Contact information",
    },
    form: {
      titleDemo: "Book a free demo",
      subtitleDemo: "Fill in the form and we'll get back to you to schedule a time that suits you.",
      titleContact: "Send a message",
      subtitleContact: "Describe your matter and we'll get back to you as soon as possible.",
      submitDemo: "Book demo",
      submitting: "Sending...",
      success: "Thank you! We've received your message and will get back to you shortly.",
      error: "Something went wrong. Please try again or contact us via email.",
      placeholders: {
        company: "Company Ltd",
        name: "John Smith",
        email: "john@company.com",
        role: "Logistics Manager",
        interest: "Select an option",
        messageDemo: "Tell us briefly about your business and what you'd like to see in the demo...",
        messageContact: "Tell us more about your enquiry...",
      },
    },
  },
  illustrations: {
    keyFactors: "Key factors",
    joinCompanies: "Join 900+ companies already using Geposit",
    zoneA: "Zone A",
    zoneB: "Zone B",
    zoneC: "Zone C",
    searchAddress: "Search address…",
    processing: "Processing…",
    completed: "completed",
    analysisResult: "Analysis result",
    addresses: "addresses",
    correctedAddresses: "Corrected addresses",
    addressFile: "address-cleansing.csv",
  },
  seo: {
    tagline: "Geographic control over your business",
    home: "Geposit offers address validation, address cleansing and geographic zoning for the entire Nordics. Modern services built on accurate and up-to-date data.",
    losningar: "Solutions for companies working with transport, field service and e-commerce. Validate addresses, create zones and optimise your workflows with Geposit.",
    losningarTransport: "Address validation and zoning for transport companies and freight forwarders. Create shipping zones, optimise routes and ensure correct delivery addresses.",
    losningarService: "Manage service areas and field staff with Geposit. Create responsibility areas on a map and validate addresses for service technicians across the Nordics.",
    losningarEcommerce: "Address validation and delivery control for e-commerce. Reduce returns and incorrect deliveries with Geposit's API and address cleansing.",
    produkter: "Geposit's services: Atlas for map-based zoning, Valid for address validation via API and Address Cleansing for registry maintenance in the Nordics.",
    atlas: "Atlas – Geposit's mapping service for geographic zoning. Create sales districts, shipping zones and service areas based on postcodes and geodata.",
    valid: "Valid – API for address validation and postcode verification in Sweden, Norway, Denmark and Finland. Autocomplete and real-time address checks.",
    adresstwatt: "Cleanse and quality-assure your address registers in the Nordics. Geposit's Address Cleansing analyses and corrects inaccurate addresses and postcodes automatically.",
    kundcase: "See how 900+ companies use Geposit for geographic control, better address quality and optimised delivery zones across the Nordics.",
    information: "News, updates and documents from Geposit on geographic data, address quality and area management in the Nordics.",
    nyheter: "News and updates from Geposit on address validation, new features and changes in Nordic address data.",
    villkor: "Terms and conditions for Geposit AB. Information on licensing, liability and use of our services and APIs.",
    bestall: "Order Geposit's services: Atlas, Valid and Address Cleansing. Choose the package that suits your needs for address management in the Nordics.",
    omGeposit: "Geposit is the Nordics' leading geodata platform. Since 2016, we help companies with address validation and geographic analysis. Part of the Movement Software group.",
    kontakt: "Contact Geposit AB in Stockholm. We help you with address validation, zoning and geodata services for the Nordics.",
    integritetspolicy: "Geposit's privacy policy. How we handle personal data in accordance with GDPR.",
    developer: "API documentation and developer resources for Geposit's Valid API. Integrate address validation and postcode verification into your system.",
  },
  order: {
    pageTitle: "Order",
    pageDescription: "Order Atlas or Valid from Geposit. Configure packages, add-ons and enter customer and billing details.",
    selectProduct: "Select product",
    selectProductDesc: "Which product would you like to order?",
    chooseThis: "Choose this",
    configureAtlas: "Configure Atlas",
    configureAtlasDesc: "Select package and configure your subscription.",
    configureValid: "Configure Valid",
    configureValidDesc: "Select package and optional add-ons.",
    choosePlan: "Choose plan",
    perMonth: "/mo",
    binding12: "12-month commitment",
    binding3: "3-month commitment",
    perMonthShort: "mo",
    adminUsers: "Admin users",
    adminUsersDesc: "1 included. Extra: 295 SEK/mo",
    viewerUsers: "Viewer users",
    viewerUsersDesc: "0 included. Extra: 95 SEK/mo",
    maps: "Maps",
    mapsDesc: "1 included. Extra: 495 SEK/mo",
    countries: {
      title: "Countries",
      desc: "At least 1 country required. First included, extra: 1,000 SEK/mo",
      se: "Sweden",
      no: "Norway",
      dk: "Denmark",
      fi: "Finland",
    },
    routing: {
      title: "Routing add-on",
      desc: "Add Routing for route calculation and reach area analysis directly in Atlas.",
      includedNote: "Routing Small is included at no extra cost with Atlas Medium and Medium 3-mo. You can upgrade to Medium or Large for more calculations.",
      nonePrice: "No add-on",
      included: "Included",
      upgradeNote: "upgrade",
      tiers: {
        none: { name: "None", short: "Without Routing" },
        small: { name: "Small", short: "Drive time between 2 points + reach areas" },
        medium: { name: "Medium", short: "Multi-stop, advanced vehicles, more calculations" },
        large: { name: "Large", short: "Highest volumes for large-scale planning" },
      },
    },
    addons: "Add-ons",
    addonsDesc: "Select which add-ons you want on top of the base package.",
    webPortal: {
      label: "I want access to the web portal to search addresses manually",
      desc: "Specify who should get login access to the search service in the web interface.",
      usersDesc: "Add the people who should have access to the web portal (max 10).",
      addUser: "Add person",
    },
    userDetails: "User details",
    userDetailsDesc: "Enter name, email and role for each user.",
    adminUser: "Admin user",
    viewerUser: "Viewer",
    userName: "Name",
    userNamePlaceholder: "First Last",
    userEmail: "Email",
    userEmailPlaceholder: "name@company.com",
    userRole: "Role",
    userRolePlaceholder: "e.g. Area manager",
    noUsersNeeded: "No user details needed for this product.",
    customerDetails: "Customer & billing details",
    customerDetailsDesc: "Fill in your contact, company and billing information.",
    contactInfo: "Contact details",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone",
    email: "Email",
    companyInfo: "Company details",
    companyName: "Company name",
    orgNumber: "Organisation number",
    orgNumberHint: "10 digits, pad with zeros if needed",
    address: "Address",
    postalCode: "Postal code",
    city: "City",
    country: "Country",
    billingInfo: "Billing details",
    copyFromCompany: "Copy from company details",
    billingAddress: "Billing address",
    reference: "Your reference",
    referenceHint: "E.g. name of contact person",
    poNumber: "Reference/PO number",
    poNumberHint: "E.g. internal purchase order number. If you wish to change your PO number in the future, you must notify us.",
    invoiceMethod: "Invoice method",
    invoiceMethods: {
      email: "Email",
      einvoice: "E-invoice",
      letter: "Letter",
    },
    invoiceEmail: "Invoice email",
    peppolId: "PEPPOL ID",
    peppolIdHint: "If no PEPPOL ID, choose another option",
    letterNotice: "The invoice will be sent to the specified billing address. An invoicing fee applies.",
    vatNumber: "VAT number",
    termsPrefix: "I accept the",
    termsLink: "terms and conditions",
    summary: "Summary",
    basePlan: "(base plan)",
    total: "Total",
    validAddonsNote: "Add-on prices are per unit and added on top of the base package.",
    step: "Step",
    back: "Back",
    next: "Next",
    submitOrder: "Complete order",
    submitting: "Submitting...",
    reviewTitle: "Review your order",
    reviewDesc: "Make sure everything looks correct before submitting your order.",
    reviewProduct: "Order",
    reviewProductLabel: "Product",
    reviewConfirmNote: "By completing your order, you confirm that the information above is correct. You will receive an order confirmation by email.",
    saveSection: "Save",
    confirmationTitle: "Thank you for your order!",
    confirmationDesc: "We have received your order and will get back to you shortly with a confirmation via email.",
    orderNumberLabel: "Order number",
    confirmationProductLabel: "Product ordered",
    confirmationEmailSent: "Confirmation sent",
    confirmationEmailSentDesc: "An order confirmation has been sent to {{email}}.",
    confirmationNextStep: "What happens next?",
    confirmationNextStepDesc: "We will process your order and get back to you as soon as possible with more information.",
    backToHome: "Back to home",
    errorTitle: "Something went wrong",
    errorDesc: "Your order could not be submitted. Please try again or contact us.",
    adresstwatt: {
      formTitle: "Address washing enquiry",
      formDesc: "Tell us about your needs and we'll get back to you with a proposal.",
      tabContact: "Contact details",
      tabBilling: "Customer & billing details",
      messageLabel: "Describe your needs",
      messagePlaceholder: "E.g. number of addresses, type of data, timeline...",
      billingInfoNote: "You don't need to fill this in now. We may ask you to complete these details after we've spoken with you.",
      confirmationTitle: "Thank you for your enquiry!",
      confirmationDesc: "We have received your address washing enquiry and will get back to you shortly.",
      confirmationNextStepDesc: "We will analyse your needs and contact you with a proposal and pricing.",
      submitRequest: "Send enquiry",
    },
    share: "Share",
    shareOrder: "Share order",
    shareCopied: "Link copied!",
    shareCopiedDesc: "The configuration link has been copied to clipboard.",
    shareFullCopied: "Order link copied!",
    shareFullCopiedDesc: "The link contains all details. The customer only needs to accept terms.",
    sharedBanner: "This order has been prepared for you. Review the selections and proceed to complete.",
    sharedFullBanner: "The order has been filled in for you. Review the details, accept the terms and submit.",
  },
  villkor: {
    hero: {
      tagline: "Documents & terms",
      title: "Clear terms for a secure partnership",
      subtitle: "Here you'll find a summary of the most important points in our terms, as well as the option to download the full documents.",
    },
    summary: {
      title: "Summary of our terms",
      subtitle: "The most important points are summarised below. The complete terms can be downloaded further down the page.",
      disclaimer: "The above is a simplified summary and does not replace the full terms. In case of any discrepancy, the signed agreement always applies.",
      items: {
        contract: { title: "Contract term & termination", description: "Agreements run for the chosen binding period and renew automatically unless terminated in writing at least 3 months before the end of the period." },
        payment: { title: "Payment & pricing", description: "Invoicing is done in advance. Prices are adjusted annually and communicated in advance via the website." },
        data: { title: "Data & confidentiality", description: "Geposit handles customer data confidentially and in accordance with GDPR. Customer data is never shared with third parties without consent." },
        responsibility: { title: "Liability & guarantees", description: "Geposit is responsible for the service functioning according to specification, with certain limitations of liability. See the full terms for more information." },
        availability: { title: "Availability & SLA", description: "The service is provided with high availability. Planned downtime is announced in advance and maintenance windows take place outside office hours." },
        usage: { title: "Use & limitations", description: "The service may be used within the scope of the agreement. Misuse, automated data extraction or resale/redistribution without an agreement is not permitted." },
      },
    },
    download: {
      title: "Download full terms",
      subtitle: "Here you can download our complete agreement documents in PDF format.",
      open: "Open",
      empty: "No documents available at the moment.",
    },
    cta: {
      title: "Questions about our terms?",
      subtitle: "Get in touch and we'll help you understand how the terms apply to your specific business.",
      button: "Contact us",
    },
  },
  certifications: {
    title: "Certifications",
    subtitle: "We work systematically with quality, environment and information security.",
    iso9001: { title: "ISO 9001", description: "Quality management" },
    iso14001: { title: "ISO 14001", description: "Environmental management" },
    iso27001: { title: "ISO 27001", description: "Information security" },
  },
  solutionExtras: {
    transport: {
      heroHighlights: [
        "Define and visualise delivery zones with postal code precision in Atlas",
        "Validate every address before dispatch with Valid",
        "Keep your customer register clean with Address Cleansing",
      ],
      challengeImpact: "Incorrect addresses and unclear delivery zones cost transport companies millions every year in returns, failed deliveries and delays.",
      productBullets: {
        atlas: ["Build delivery zones with postal code polygons as building blocks", "Visualise and analyse areas per terminal, driver or region"],
        valid: ["Validate delivery addresses in real time before the parcel leaves the terminal", "Catch incorrect and incomplete addresses immediately at entry"],
        adresstwatt: ["Cleanse customer registers regularly for accurate address data", "Identify and correct outdated addresses automatically"],
      },
    },
    service: {
      heroHighlights: [
        "Define precise service areas with postal code precision in Atlas",
        "Ensure correct customer addresses at booking with Valid",
        "Keep your service platform clean with Address Cleansing",
      ],
      challengeImpact: "Overlapping service areas and incorrect addresses cost service companies on average 15–20% of their operational capacity in failed visits and duplicated work.",
      productBullets: {
        atlas: ["Build service areas with postal code polygons – no overlaps", "Analyse workload based on customer count and geography"],
        valid: ["Validate service addresses directly at booking to avoid failed visits", "Give customers accurate address suggestions in real time via autocomplete"],
        adresstwatt: ["Clean incorrect addresses in your customer register regularly", "Ensure the service platform is built on accurate data"],
      },
    },
    ecommerce: {
      heroHighlights: [
        "Validate customer addresses directly at checkout with Valid",
        "Define delivery zones with postal code precision in Atlas",
        "Expand safely to Nordic markets with accurate data",
      ],
      challengeImpact: "Incorrect addresses in e-commerce mean up to 5% of all deliveries fail – with returns costing 3–5 times more than the original shipping.",
      productBullets: {
        atlas: ["Build delivery zones with postal code polygons per region, shipping method or price zone", "Visualise your delivery areas and export postal code lists"],
        valid: ["Integrate autocomplete directly into your checkout for flawless address entry", "Validate addresses in real time for Sweden, Norway, Denmark and Finland"],
        adresstwatt: ["Quality-assure customer registers ahead of campaigns and mailings", "Identify duplicates and outdated addresses in bulk"],
      },
    },
  },
  pricingDb: {
    nordicValidation: "Address validation across the Nordics",
    autocomplete: "Autocomplete for street names, postal codes and cities",
    adminControl: "Control of county, municipality, parish and district",
    bindingTime12: "12-month binding period",
    oneAdminMapCountry: "1 admin, 1 map, 1 country",
    importOwnData: "Import your own data onto the map",
    createManageAreas: "Create, view and manage areas",
    integrateApi: "Integrate with your systems via API",
    export2000Postal: "Export 2,000 postal codes/mo",
    exportUnlimitedPostal: "Export unlimited postal codes",
    routingSmallIncluded: "Routing Small included",
    validationsPerMonth: "Validations/mo",
    autocompletePerMonth: "Autocomplete/mo",
    extraValidation: "Extra validation",
    extraAutocomplete: "Extra autocomplete",
    geocodeAddress: "Geocoding address",
    geocodePostal: "Geocoding postal code",
    propertyDesignation: "Property designation",
    uuid: "UUID",
    desoRegso: "DeSO/RegSO",
    extraCountry: "Extra country",
    extraMap: "Extra map",
    extraAdmin: "Extra admin",
    extraViewer: "Extra viewer",
    exportPostal: "Postal code export",
    exportCoordinates: "Coordinate export",
    perQuote: "On request",
    included: "Included",
  },
};

